import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import './Footer.css';
import { getCompanyFromUrl, isCompanySubdomain } from '../utils/company';
import { legalPagesApi, type SocialMediaLink } from '../services/legalPagesApi';

const Footer = () => {
  const location = useLocation();
  const [socialLinks, setSocialLinks] = useState<SocialMediaLink[]>([]);
  const [footerDescription, setFooterDescription] = useState<string>('');
  const [socialHeading, setSocialHeading] = useState<string>('Folgen Sie uns');

  function getFallbackSocialLinks(): SocialMediaLink[] {
    return [
      {
        id: '1',
        platform: 'linkedin',
        url: 'https://linkedin.com/company/the-digital-workforce-group-ag',
        order: 1
      },
      {
        id: '2',
        platform: 'twitter',
        url: 'https://x.com/workforce2018',
        order: 2
      },
      {
        id: '3',
        platform: 'instagram',
        url: 'https://www.instagram.com/thedigitalworkforcegroup/?hl=de',
        order: 3
      },
      {
        id: '4',
        platform: 'xing',
        url: 'https://www.xing.com/pages/thedigitalworkforcegroupag',
        order: 4
      }
    ];
  }

  // Determine current company (prefer subdomain)
  const getCompany = (): string => getCompanyFromUrl();

  // Fetch social media links on mount
  useEffect(() => {
    const loadSocialLinks = async () => {
      try {
        const data = await legalPagesApi.getSocialMediaLinks();
        setSocialLinks(data.links.sort((a, b) => a.order - b.order));
        if (data.description) setFooterDescription(data.description);
        if (data.socialHeading) setSocialHeading(data.socialHeading);
      } catch (error) {
        console.error('Failed to load social media links, using fallback', error);
        // Fallback to hardcoded links if API fails
        setSocialLinks(getFallbackSocialLinks());
      }
    };
    loadSocialLinks();
  }, []);

  // Generate company-specific route
  const getCompanyRoute = (page: string): string => {
    const company = getCompany();
    if (isCompanySubdomain()) return `/${page}`;
    return `/${company}/${page}`;
  };

  const handleLogoClick = () => {
    // If we're already on the homepage, scroll to top
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Get company-specific branding information
  const getCompanyBranding = () => {
    const company = getCompany();

    switch (company) {
      case 'talentschmiede':
        return {
          logo: '/talentschmiedeLogo.png',
          alt: 'Talentschmiede',
          tagline: 'Talentschmiede',
          description: 'Ihr Partner für maßgeschneiderte IT-Lösungen und digitale Transformation.'
        };
      case 'digitaldavid':
        return {
          logo: '/digitaldavidlogo.png',
          alt: 'Digital David',
          tagline: 'Digital David AG',
          description: 'Spezialisiert auf innovative Technologien und digitale Beratung.'
        };
      default: // 'dwg'
        return {
          logo: '/logo-long.png',
          alt: 'Digital Workforce Group',
          tagline: 'The Digital Workforce Group AG',
          description: 'Ihr Partner für IT-Consulting und Agile Transformation im Finanzsektor.'
        };
    }
  };

  const branding = getCompanyBranding();
  const homeLinkLabel = `Zur Startseite von ${branding.alt}`;

  // Get SVG icon for each platform
  const getSocialIcon = (platform: string) => {
    switch (platform) {
      case 'linkedin':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
          </svg>
        );
      case 'twitter':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        );
      case 'instagram':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
          </svg>
        );
      case 'xing':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.188 0c-.517 0-.741.325-.927.66 0 0-7.455 13.224-7.702 13.657.015.024 4.919 9.023 4.919 9.023.17.308.436.66.967.66h3.454c.211 0 .375-.078.463-.22.089-.151.089-.346-.009-.536l-4.879-8.916c-.004-.006-.004-.016 0-.022L22.139.756c.095-.191.095-.387.006-.535C22.056.078 21.894 0 21.686 0h-3.498zM3.648 4.74c-.211 0-.385.074-.473.216-.09.149-.078.339.02.531l2.34 4.05c.004.01.004.016 0 .021L1.86 16.051c-.099.188-.099.389 0 .534.088.145.26.215.473.215h3.187c.544 0 .755-.308.909-.585 0 0 3.631-6.693 3.749-6.881l-2.378-4.135c-.171-.315-.432-.659-.963-.659H3.648z"/>
          </svg>
        );
      case 'facebook':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
        );
      case 'youtube':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
          </svg>
        );
      default:
        return null;
    }
  };

  // Get platform label
  const getPlatformLabel = (platform: string): string => {
    const labels: Record<string, string> = {
      linkedin: 'LinkedIn',
      twitter: 'X (Twitter)',
      instagram: 'Instagram',
      xing: 'Xing',
      facebook: 'Facebook',
      youtube: 'YouTube'
    };
    return labels[platform] || platform;
  };

  return (
    <footer className="footer-section">
      <div className="container">
        {/* Main Footer Content */}
        <div className="footer-main">
          <div className="footer-grid">
            
            {/* Brand Column */}
            <div className="footer-brand">
              <Link
                to="/"
                className="footer-logo-link"
                onClick={handleLogoClick}
                aria-label={homeLinkLabel}
                title={homeLinkLabel}
              >
                <img
                  src={branding.logo}
                  alt={branding.alt}
                  className="footer-logo-img"
                  style={{
                    height: '40px',
                    width: 'auto',
                    objectFit: 'contain'
                  }}
                />
              </Link>
              <p className="footer-tagline">
                {branding.tagline}
              </p>
              <p className="footer-description">
                {footerDescription || branding.description}
              </p>

              {/* Social Links - Dynamic from Backend */}
              {socialLinks.length > 0 && (
                <>
                  <p className="footer-social-heading">{socialHeading}</p>
                  <nav className="footer-social" aria-label="Social-Media-Profile">
                    {socialLinks.map((link) => (
                      <a
                        key={link.id}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer external"
                        className="footer-social-link"
                        aria-label={`${getPlatformLabel(link.platform)} (öffnet in neuem Tab)`}
                        title={`${getPlatformLabel(link.platform)} (öffnet in neuem Tab)`}
                      >
                        {getSocialIcon(link.platform)}
                      </a>
                    ))}
                  </nav>
                </>
              )}
            </div>


            {/* Legal */}
            <div className="footer-column">
              <h3 className="footer-column-title">Rechtliches</h3>
              <nav aria-label="Rechtliche Hinweise">
                <ul className="footer-links">
                  <li>
                    <Link to={getCompanyRoute('impressum')} className="footer-link">
                      Impressum
                    </Link>
                  </li>
                  <li>
                    <Link to={getCompanyRoute('datenschutz')} className="footer-link">
                      Datenschutz
                    </Link>
                  </li>
                  <li>
                    <Link to={getCompanyRoute('datenschutz-bewerber')} className="footer-link">
                      Datenschutz für Bewerber
                    </Link>
                  </li>
                  <li>
                    <Link to={getCompanyRoute('faq')} className="footer-link">
                      FAQ
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>

          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
